import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Signup from './components/auth/Signup';
import Login from './components/auth/Login';
import ForgotPassword from './components/auth/ForgotPassword';
import Dashboard from './components/dashboard/Dashboard';
import JobFeed from './components/jobs/JobFeed';
import CompanyJobs from './components/jobs/CompanyJobs';
import Profile from './components/profile/Profile';
import AdminDashboard from './components/admin/AdminDashboard';
import Navbar from './components/layout/Navbar';
import EmployeeManagement from './crm/EmployeeManagement';
import EmployeeCreate from './crm/EmployeeCreate';
import EmployeeList from './crm/EmployeeList';
import AssignmentStatus from './crm/AssignmentStatus';
import EmployeeHome from './crm/EmployeeHome';
import EmployeeAssignments from './crm/EmployeeAssignments';
import EmployeeFollowups from './crm/EmployeeFollowups';
import CompanyUploadedResumes from './crm/CompanyUploadedResumes';
import CompanySubCompanies from './crm/CompanySubCompanies';
import CompanyRequirements from './crm/CompanyRequirements';
import CompanyFollowupReport from './crm/CompanyFollowupReport';
import CompanyRecruiterPerformance from './crm/CompanyRecruiterPerformance';
import ClientCompanyDashboard from './crm/ClientCompanyDashboard';
import ResumeUpload from './crm/ResumeUpload';
import ToastViewport from './components/common/ToastViewport';
import ApiURL from './controllers/Api';
import { toast } from './utils/toast';

export const useTheme = () => ({
  darkMode: false,
  setDarkMode: () => {},
  toggleDarkMode: () => {},
  theme: "light",
});

function AppContent() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  const clearAuthData = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userType');
    localStorage.removeItem('tokenExpiry');
    setIsLoggedIn(false);
    setUserData(null);
  };

  // Check auth on mount (LOCAL STORAGE ONLY)
  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUserData = localStorage.getItem('user');

    if (token && storedUserData) {
      try {
        const parsedUser = JSON.parse(storedUserData);
        setIsLoggedIn(true);
        setUserData(parsedUser);
      } catch (err) {
        console.error("Invalid user data in localStorage");
        clearAuthData();
      }
    } else {
      clearAuthData();
    }

    setLoading(false);
  }, []);

  // Session keep-alive/verification removed.
  // Reason: company resume upload was causing users to be logged out.
  // Token should be treated as valid until user explicitly logs out.
  useEffect(() => {
    // no-op
  }, []);

  const handleLogin = async (loginData) => {
    try {
      if (!loginData?.token || !loginData?.user) {
        throw new Error("Invalid login data");
      }

      localStorage.setItem('token', loginData.token);
      localStorage.setItem('user', JSON.stringify(loginData.user));

      setIsLoggedIn(true);
      setUserData(loginData.user);

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'Failed to login' };
    }
  };

  const handleLogout = () => {
    clearAuthData();
  };

  const normalizedRole = String(userData?.role || userData?.employee?.role || '').toLowerCase();
  const isAdminUser = Boolean(userData?.is_admin) || normalizedRole === 'admin';
  const isEmployeeUser = normalizedRole === 'employee';
  const isSubCompanyUser = normalizedRole === 'sub_company' || userData?.user_type === 'sub_company';
  const isCompanyUser = userData?.user_type === 'company';
  const employeeDefaultPath = userData?.can_view_dashboard === false
    ? '/employee-lead-report'
    : '/dashboards';
  const defaultAuthenticatedPath = isAdminUser
    ? '/admin/resumes'
    : isSubCompanyUser
    ? '/company/client-dashboard'
    : isCompanyUser
    ? '/company/dashboard'
    : isEmployeeUser
    ? employeeDefaultPath
    : '/dashboard';

  // Protected Route
  const ProtectedRoute = ({ children }) => {
    if (loading) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      );
    }

    if (!isLoggedIn) {
      return <Navigate to="/login" />;
    }

    return children;
  };

  // Public Route
  const PublicRoute = ({ children }) => {
    if (loading) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      );
    }

    if (isLoggedIn) {
      return <Navigate to={defaultAuthenticatedPath} />;
    }

    return children;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading application...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ToastViewport />
      <div className="min-h-screen bg-slate-50">
        <Navbar onLogout={handleLogout} userData={userData} />
        <div className="pt-16">
          <Routes>

            {/* Root redirect */}
            <Route path="/" element={<Navigate to="/jobs" replace />} />

            {/* Public Routes */}
            <Route
              path="/signup"
              element={
                <PublicRoute>
                  <Signup onLogin={handleLogin} />
                </PublicRoute>
              }
            />

            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login onLogin={handleLogin} />
                </PublicRoute>
              }
            />

            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              }
            />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/userDashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/jobs"
              element={
                <JobFeed
                  isLoggedIn={isLoggedIn}
                  userData={userData}
                />
              }
            />

            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <Navigate to="/admin/resumes" replace />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/resumes"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/users"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/companies"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/jobs"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin-dashboard"
              element={
                <ProtectedRoute>
                  <Navigate to="/admin/resumes" replace />
                </ProtectedRoute>
              }
            />

            <Route
              path="/company"
              element={
                <ProtectedRoute>
                  <Navigate to="/company/dashboard" replace />
                </ProtectedRoute>
              }
            />

            <Route
              path="/company/dashboard"
              element={
                <ProtectedRoute>
                  <AssignmentStatus />
                </ProtectedRoute>
              }
            />

            <Route
              path="/company/employees/create"
              element={
                <ProtectedRoute>
                  <EmployeeManagement />
                </ProtectedRoute>
              }
            />

            <Route
              path="/company/employees"
              element={
                <ProtectedRoute>
                  <EmployeeManagement />
                </ProtectedRoute>
              }
            />

            <Route
              path="/company/resume-status"
              element={
                <ProtectedRoute>
                  <Navigate to="/company/dashboard" replace />
                </ProtectedRoute>
              }
            />

            <Route
              path="/company/recruiter-performance"
              element={
                <ProtectedRoute>
                  <CompanyRecruiterPerformance />
                </ProtectedRoute>
              }
            />

            <Route
              path="/company/followup-report"
              element={
                <ProtectedRoute>
                  <CompanyFollowupReport />
                </ProtectedRoute>
              }
            />

            <Route
              path="/company/assign-resumes"
              element={
                <ProtectedRoute>
                  <Navigate to="/company/dashboard" replace />
                </ProtectedRoute>
              }
            />

            <Route
              path="/company/uploaded-resumes"
              element={
                <ProtectedRoute>
                  <CompanyUploadedResumes />
                </ProtectedRoute>
              }
            />

            <Route
              path="/company/upload-resumes"
              element={
                <ProtectedRoute>
                  <ResumeUpload />
                </ProtectedRoute>
              }
            />

            <Route
              path="/company/jobs"
              element={
                <ProtectedRoute>
                  <CompanyJobs />
                </ProtectedRoute>
              }
            />

            <Route
              path="/company/sub-companies"
              element={
                <ProtectedRoute>
                  <CompanySubCompanies />
                </ProtectedRoute>
              }
            />

            <Route
              path="/company/client-dashboard"
              element={
                <ProtectedRoute>
                  <ClientCompanyDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/company/requirements"
              element={
                <ProtectedRoute>
                  <CompanyRequirements />
                </ProtectedRoute>
              }
            />

            <Route
              path="/addEmployee"
              element={
                <ProtectedRoute>
                  <EmployeeCreate />
                </ProtectedRoute>
              }
            />

            <Route
              path="/viewEmployee"
              element={
                <ProtectedRoute>
                  <EmployeeList />
                </ProtectedRoute>
              }
            />

            <Route
              path="/resume-status"
              element={
                <ProtectedRoute>
                  <AssignmentStatus />
                </ProtectedRoute>
              }
            />

            <Route
              path="/allLead"
              element={
                <ProtectedRoute>
                  <Navigate to="/admin/resumes" replace />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboards"
              element={
                <ProtectedRoute>
                  <EmployeeHome />
                </ProtectedRoute>
              }
            />

            <Route
              path="/employee/upload-resumes"
              element={
                <ProtectedRoute>
                  <ResumeUpload />
                </ProtectedRoute>
              }
            />

            <Route
              path="/employee/resume-library"
              element={
                <ProtectedRoute>
                  <CompanyUploadedResumes />
                </ProtectedRoute>
              }
            />

            <Route
              path="/employee/sub-companies"
              element={
                <ProtectedRoute>
                  <CompanySubCompanies />
                </ProtectedRoute>
              }
            />

            <Route
              path="/employee/jobs"
              element={
                <ProtectedRoute>
                  <CompanyJobs />
                </ProtectedRoute>
              }
            />

            <Route
              path="/employee-lead-report"
              element={
                <ProtectedRoute>
                  <EmployeeAssignments />
                </ProtectedRoute>
              }
            />

            <Route
              path="/follow-up"
              element={
                <ProtectedRoute>
                  <EmployeeFollowups />
                </ProtectedRoute>
              }
            />

            <Route
              path="/employee-followup-report"
              element={
                <ProtectedRoute>
                  <EmployeeFollowups />
                </ProtectedRoute>
              }
            />

            {/* Fallback */}
            <Route
              path="*"
              element={
                <Navigate to={isLoggedIn ? defaultAuthenticatedPath : "/jobs"} />
              }
            />
          </Routes>
        </div>
      </div>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
